<script setup lang="ts">

import TopLevelMenu from '~/components/menu/TopLevelMenu.vue';
import { useSlideoverToggle } from '~/composables/useSlideoverToggle';

defineProps<{
  onLogout:() => void;
}>();

const activeMenu = ref<string | null>(null);

const linkStatus = (key: string, isSelected: Ref<boolean>) => {
  const show = computed(() => (activeMenu.value === key) || isSelected.value);
  const toggle = () => {
    activeMenu.value = activeMenu.value === key ? null : key;
  };
  return {
    status: {
      isSelected,
      show,
      toggle,
      key,
    },
  };
};

const uuid = defineModel<string>('uuid', { required: true });
const { user, entities } = useSession();
const { entity } = useEntity();

const route = useRoute();
const routeName = computed(() => String(route.name));
const { status: dashboard } = linkStatus(
  'dashboard',
  computed(() => [
    'uuid',
  ].includes(routeName.value)),
);
const { status: billing } = linkStatus(
  'billing',
  computed(() => [
    'renters-billing',
    'tuition-billing',
    'renters-billing-history',
    'tuition-billing-history',
  ].includes(routeName.value)),
);

const { status: policies } = linkStatus(
  'policies',
  computed(() => [
    'uuid-renters-policyNumber',
    'uuid-tuition-policyNumber',
  ].includes(routeName.value)),
);

const { status: info } = linkStatus(
  'info',
  computed(() => [
    'uuid-info',
  ].includes(routeName.value)),
);

const { status: account } = linkStatus(
  'settings',
  computed(() => [
    'account',
  ].includes(routeName.value)),
);

const { slideoverState, setSlideoverState } = useSlideoverToggle();
watch(routeName, () => {
  activeMenu.value = null;
});

</script>

<template>
  <aside class="bg-(--color-primary-800) mx-auto grow relative p-5 w-full">
    <nav class="h-full">
      <ul class="flex flex-col h-full list-none">
        <li class="flex flex-col gap-5 grow">
          <button
            class="lg:hidden text-primary-200 cursor-pointer absolute top-0 right-1"
            type="button"
            aria-label="Close menu"
            @click="setSlideoverState(!slideoverState)"
            @keyup.enter="setSlideoverState(!slideoverState)"
          >
            <UiFaIcon :icon="['fas', 'xmark']" size="2xl" aria-hidden />
          </button>
          <div class="mx-auto">
            <NuxtLink :to="`/${uuid}`" aria-label="GradGuard home">
              <UiSvgGradGuard :one-color="true" class="w-auto h-10 text-white" aria-hidden />
            </NuxtLink>
          </div>
          <div v-if="entities.length > 1">
            <div class="text-white font-semibold font-sans-serif mb-2">
              Viewing information for:
            </div>
            <LayoutSidebarEntities
              v-model="uuid"
              class="hover:bg-primary-500"
              :entities="entities.map((entity) => ({
                id: entity.id,
                name: `${entity.first_name} ${entity.last_name}`,
                uuid: entity.uuid,
              }))"
            />
          </div>
          <ul class="flex flex-col gap-5 list-none">
            <li>
              <TopLevelMenu
                title="Dashboard"
                :icon="['fal', 'grid-2']"
                icon-class="ml-2 mr-1"
                :url="`/${uuid}`"
                :is-selected="dashboard.isSelected.value"
                @click="setSlideoverState(false)"
              />
            </li>
            <li>
              <TopLevelMenu
                title="Policies"
                :icon="['fal', 'file-contract']"
                icon-class="ml-3 mr-3"
                :show="policies.show.value"
                :click-func="policies.toggle"
                aria-label="Open the Policies menu"
                data-cy="menu-policies"
              >
                <div class="flex flex-col gap-4">
                  <LayoutSidebarLastRentersPolicy
                    v-if="entity.lastRentersPolicy"
                    :policy="entity.lastRentersPolicy"
                    route-name="uuid-renters-policyNumber"
                    :uuid="uuid"
                  />
                  <LayoutSidebarLastTuitionPolicy
                    v-if="entity.lastTuitionPolicy"
                    :policy="entity.lastTuitionPolicy"
                    route-name="uuid-tuition-policyNumber"
                    :uuid="uuid"
                  />
                </div>
              </TopLevelMenu>
            </li>
            <li>
              <TopLevelMenu
                title="Insured Info"
                :icon="['fal', 'user']"
                icon-class="ml-2.5 mr-2.5"
                :show="info.show.value"
                :click-func="info.toggle"
                data-cy="menu-insured-info"
              >
                <MenuLink
                  :link="{ name: `${entity.first_name} ${entity.last_name}`, url: `/${uuid}/info` }"
                  :icon="['fal', 'user-graduate']"
                  data-cy="menu-contact-info"
                  :is-selected="info.isSelected.value"
                />
              </TopLevelMenu>
            </li>
            <template v-if="user">
              <li class="h-[1px] bg-primary-50 mb-3" aria-hidden="true" />
              <li
                v-if="user && (user.hasKaminoPaymentMethod || user.hasSkypadPaymentMethod || user.hasTuition)"
              >
                <TopLevelMenu
                  title="Billing"
                  :icon="['fal', 'credit-card']"
                  icon-class="ml-1.5 mr-2"
                  :click-func="billing.toggle"
                  :show="billing.show.value"
                  data-cy="menu-billing"
                >
                  <div v-if="billing.show">
                    <MenuLink
                      v-if="user.hasKaminoPaymentMethod || user.hasSkypadPaymentMethod"
                      :link="{ name: 'Renters billing', url: '/renters/billing' }"
                      data-cy="menu-billing-renters"
                      :is-selected="routeName === 'renters-billing'"
                    />
                    <MenuLink
                      v-if="user.hasTuition"
                      :link="{ name: 'Tuition billing', url: '/tuition/billing' }"
                      data-cy="menu-billing-tuition"
                      :is-selected="routeName === 'tuition-billing'"
                    />
                  </div>
                </TopLevelMenu>
              </li>
              <li>
                <TopLevelMenu
                  title="Account"
                  :icon="['fal', 'grid-2']"
                  icon-class="ml-2 mr-1"
                  url="/account"
                  :is-selected="account.isSelected.value"
                  @click="setSlideoverState(false)"
                />
              </li>
            </template>
          </ul>
        </li>
        <li class="mt-auto flex flex-col gap-5 pt-4">
          <TopLevelMenu
            title="Support"
            :icon="['fal', 'headset']"
            icon-class="ml-2 mr-2"
            :click-func="() => navigateTo('https://gradguard.com/support', {
              external: true,
              open: {
                target: '_blank',
              },
            })"
          />
          <TopLevelMenu
            title="Logout"
            :icon="['fal', 'door-open']"
            icon-class="ml-1.5 mr-2"
            :click-func="onLogout"
          />
        </li>
      </ul>
    </nav>
  </aside>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
